import { useEffect, useMemo } from 'react';
import { TrendingUp, TrendingDown, Minus, AlertCircle } from 'lucide-react';

import { useDataState } from '@/hooks/use-data-state';
import { getMarketDataProvider } from '@/lib/market-data';
import { mergeQuoteWithAI } from '@/lib/quote-merge';
import { cn, formatRelativeTime } from '@/lib/utils';
import { SETUP_BARS } from '@/lib/v6/setup-state';

interface SymbolQuoteCellProps {
  symbol: string;
  /**
   * Meldet den aktuellen Kurs nach oben (23.09.2026, Kaufzonen-Balken) — ohne
   * einen zweiten Kursabruf: derselbe Preis, den diese Zelle ohnehin schon
   * für sich selbst holt.
   */
  onQuoteComputed?: (price: number | null) => void;
}

const UNSUPPORTED_SUFFIXES = [':XETRA', ':FRA', ':SWX', '.DE', '.PA'];

function isLikelyUnsupportedFormat(symbol: string): boolean {
  const upper = symbol.toUpperCase();
  return UNSUPPORTED_SUFFIXES.some((s) => upper.includes(s));
}

/**
 * Zeigt Kurs / Tagesveränderung / Drawdown vom 52W-Hoch eines Symbols.
 * Stille Phase (Null-Provider): zeigt "–" mit Begründung.
 * Symbol-Format wird vom Provider nicht unterstützt: klare Info-Anzeige.
 * Live: Quote + Mini-Stats. Loading: Skeleton.
 */
export function SymbolQuoteCell({ symbol, onQuoteComputed }: SymbolQuoteCellProps) {
  const provider = useMemo(() => getMarketDataProvider(), []);
  const isUnsupportedFormat = useMemo(() => isLikelyUnsupportedFormat(symbol), [symbol]);

  const { state } = useDataState({
    queryKey: ['quote', symbol, provider.name],
    queryFn: (signal) => provider.getQuote(symbol, signal).then((s) => s.data),
    enabled: provider.is_live && !isUnsupportedFormat,
    ttlSeconds: 60,
    isEmpty: (q) => !q || q.current_price === 0,
  });

  useEffect(() => {
    onQuoteComputed?.(state.data?.current_price ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.data?.current_price]);

  // W99-Hotfix (BRK.B) — 52W-Hoch aus OHLCV-Bars statt roher Provider-Quote.
  // Finnhub liefert für BRK.B das A-SHARE-Hoch (~775.000 statt ~516) →
  // „−99,9 % unter Hoch", seit W97 grün gefärbt = falsches Kaufsignal.
  // GLEICHER queryKey wie SymbolScoreCell → geteilter Cache, kein 2. Call.
  const ohlcv = useDataState({
    queryKey: ['ohlcv-for-score', symbol, 'daily', SETUP_BARS, provider.name],
    queryFn: (signal) => provider.getOhlcv(symbol, 'daily', SETUP_BARS, signal).then((s) => s.data),
    enabled: provider.is_live && !isUnsupportedFormat,
    ttlSeconds: 30 * 60,
    isEmpty: (b) => !b || b.length === 0,
  });
  const mergedQuote = useMemo(() => {
    const raw = state.data;
    if (!raw) return null;
    const bars = ohlcv.state.data ?? null;
    const last252 = bars && bars.length > 0 ? bars.slice(-252) : null;
    const high = last252 ? Math.max(...last252.map((b) => b.high)) : null;
    const low = last252 ? Math.min(...last252.map((b) => b.low)) : null;
    return mergeQuoteWithAI(raw, null, {
      high_52w_from_bars: high !== null && Number.isFinite(high) ? high : null,
      low_52w_from_bars: low !== null && Number.isFinite(low) ? low : null,
      drawdown_from_52w_high_pct_from_bars:
        high !== null && Number.isFinite(high) && high > 0 && raw.current_price > 0
          ? ((raw.current_price - high) / high) * 100
          : null,
    });
  }, [state.data, ohlcv.state.data]);

  if (isUnsupportedFormat) {
    return (
      <div className="text-right shrink-0 max-w-[10rem]">
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">Kurs</div>
        <div className="text-amber-400/80 inline-flex items-center gap-1 text-xs justify-end">
          <AlertCircle className="size-3" />
          Format nicht unterstützt
        </div>
        <div className="text-[9px] text-zinc-500 mt-0.5 leading-tight">
          {symbol.toUpperCase().includes('XETRA')
            ? 'XETRA-Listings via IBKR (Phase 3)'
            : 'Provider deckt Symbol nicht ab'}
        </div>
      </div>
    );
  }

  if (!provider.is_live) {
    return (
      <div className="text-right shrink-0">
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">Kurs</div>
        <div className="text-2xl font-mono text-zinc-600">–</div>
        <div className="text-[10px] text-zinc-600">noch keine Marktdaten</div>
      </div>
    );
  }

  if (state.status === 'loading' || state.status === 'idle') {
    return (
      <div className="text-right shrink-0 min-w-[8rem]">
        <div className="skeleton h-3 w-12 ml-auto mb-1" />
        <div className="skeleton h-6 w-20 ml-auto mb-1" />
        <div className="skeleton h-3 w-16 ml-auto" />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="text-right shrink-0">
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">Kurs</div>
        <div className="text-sm text-red-300/70">Fehler</div>
        <div className="text-[10px] text-red-300/60">{state.error?.code}</div>
      </div>
    );
  }

  if (state.status === 'no_data' || !state.data) {
    return (
      <div className="text-right shrink-0">
        <div className="text-[10px] uppercase tracking-wide text-zinc-500">Kurs</div>
        <div className="text-sm text-zinc-500">–</div>
        <div className="text-[10px] text-zinc-600">unbekanntes Symbol</div>
      </div>
    );
  }

  const q = mergedQuote ?? state.data;
  const isUp = q.change_pct_today > 0;
  const isDown = q.change_pct_today < 0;
  // W99-Hotfix — Drawdown aus der GEMERGTEN Quote (OHLCV-SSoT + Guard),
  // nicht mehr aus dem rohen Provider-high_52w gerechnet.
  const drawdownPct =
    q.drawdown_from_52w_high_pct !== null && q.drawdown_from_52w_high_pct !== undefined
      ? q.drawdown_from_52w_high_pct
      : q.high_52w > 0
        ? ((q.current_price - q.high_52w) / q.high_52w) * 100
        : null;

  return (
    <div className="text-right shrink-0 min-w-[8rem]">
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">Kurs</div>
      <div className="text-xl font-mono text-zinc-100 leading-none">
        {q.current_price.toFixed(2)}
      </div>
      <div
        className={cn(
          'mt-1 inline-flex items-center gap-1 text-[11px] font-mono',
          isUp ? 'text-emerald-300' : isDown ? 'text-red-300' : 'text-zinc-400'
        )}
      >
        {isUp ? (
          <TrendingUp className="size-3" />
        ) : isDown ? (
          <TrendingDown className="size-3" />
        ) : (
          <Minus className="size-3" />
        )}
        {q.change_pct_today >= 0 ? '+' : ''}
        {q.change_pct_today.toFixed(2)}%
      </div>
      {drawdownPct !== null && q.high_52w > 0 && (
        <div
          className="mt-1.5 text-[10px] text-zinc-500"
          title="Jahreshoch — höchster Kurs der letzten 12 Monate"
        >
          Jahreshoch {q.high_52w.toFixed(2)}
          <br />
          {drawdownPct >= -1 ? 'am Hoch' : `${drawdownPct.toFixed(1)}% unter Hoch`}
        </div>
      )}
      {state.last_successful_fetch_at && (
        <div className="mt-1 text-[9px] text-zinc-600">
          {formatRelativeTime(state.last_successful_fetch_at)}
        </div>
      )}
    </div>
  );
}
